import { useState, useEffect } from 'react';
import { Typography, Box, Stack, IconButton } from '@mui/material';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import ReactSpeedometer from "react-d3-speedometer";
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const OccupancyLegendItem = ({ color, borderColor, label, isActive }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      fontSize: 12,
      color: isActive ? '#111' : '#4B5563',
      mb: 0.5,
      cursor: 'pointer',
      fontWeight: isActive ? 'bold' : 'normal',
      userSelect: 'none',
      transition: 'color 0.3s ease',
    }}
  >
    <Box
      sx={{
        width: 22,
        height: 18,
        borderRadius: 1,
        border: `1px solid ${borderColor}`,
        backgroundColor: color,
        boxShadow: isActive ? `0 0 5px ${color}` : 'none',
        transition: 'box-shadow 0.3s ease',
      }}
    />
    <Typography
      component="span"
      sx={{
        fontSize: 15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {label}
    </Typography>
  </Stack>
);

const TodaysOccupancy = () => {
  const ranges = [
    { label: "0-20 % Very low", min: 0, max: 20, color: "#ff6666", borderColor: "#cc3333" },
    { label: "21-40 % Low", min: 21, max: 40, color: "#FFB347", borderColor: "#cc8400" },
    { label: "41-60 % Fair", min: 41, max: 60, color: "#cccc00", borderColor: "#999900" },
    { label: "61-80 % Good", min: 61, max: 80, color: "#3399cc", borderColor: "#007acc" },
    { label: "81-100 % Very good", min: 81, max: 100, color: "#009966", borderColor: "#007a4d" },
  ];

  const [activeIndex, setActiveIndex] = useState(0);
  const [needlePercent, setNeedlePercent] = useState(0);
  const [totalBookedBed, setTotalBookedBed] = useState(0);
  const [totalOccupiedBed, setTotalOccupiedBed] = useState(0);
  const [currentMonth, setCurrentMonth] = useState('');
  const [rawPercentage, setRawPercentage] = useState(0);
  const globalProperty = useSelector(selectGlobalProperty);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const today = new Date();
        const currentMonthYear = today.toLocaleString('default', { year: 'numeric', month: 'long' });
        const currentMonthId = today.getMonth() + 1; // 1-12
        const currentYear = today.getFullYear();
        setCurrentMonth(currentMonthYear);

        const bookDate = today.toISOString().split('T')[0];
        const checkoutDateObj = new Date(today);
        checkoutDateObj.setMonth(checkoutDateObj.getMonth() + 11);
        const checkoutDate = checkoutDateObj.toISOString().split('T')[0];

        const response = await axiosInstance.post('BedCalendar/GenerateBedCatCal', {
          PropId: globalProperty,
          BookDtFrom: bookDate,
          CheckoutDt: checkoutDate,
          Flag: 'Summary',
        });

        if (response.data.Status === 0 && response.data.Data && Array.isArray(response.data.Data.BedCatCalList)) {
          const data = response.data.Data.BedCatCalList;

          // Filter data for the current month (August 2025)
          const currentMonthData = data.filter((item) =>
            item.MonthId === currentMonthId && item.YearNm === currentYear
          );

          if (currentMonthData.length === 0) {
            setNeedlePercent(0);
            setTotalBookedBed(0);
            setTotalOccupiedBed(0);
            toast.info(`No occupancy data for ${currentMonthYear}.`);
            return;
          }

          // Calculate total booked, occupied, and beds to sale for the current month
          const totalBooked = currentMonthData.reduce((acc, item) => acc + (Number(item.BookingBeds) || 0), 0);
          const totalOccupied = currentMonthData.reduce((acc, item) => acc + (Number(item.OccBeds) || 0), 0);
          const totalBedsToSale = currentMonthData.reduce((acc, item) => acc + (Number(item.BedToSale) || 0), 0);
          // const percentage = totalBedsToSale ? ((totalBooked + totalOccupied) / totalBedsToSale) * 100 : 0;  // With (Booked + Occupied)
          const percentage = totalBedsToSale ? ((totalOccupied) / totalBedsToSale) * 100 : 0;

          setRawPercentage(percentage);
          setNeedlePercent(valueToNeedlePercent(percentage));
        } else {
          setNeedlePercent(0);
          setTotalBookedBed(0);
          setTotalOccupiedBed(0);
          toast.info('Invalid response from server.');
        }
      } catch (error) {
        setNeedlePercent(0);
        setTotalBookedBed(0);
        setTotalOccupiedBed(0);
        toast.error('Error fetching occupancy data.');
      }
    };

    fetchData();

  }, [globalProperty]);

  const totalRangeSize = ranges.reduce((acc, r) => acc + (r.max - r.min), 0);
  const arcsLength = ranges.map((r) => (r.max - r.min) / totalRangeSize);
  const cumulativeArcs = arcsLength.reduce((acc, length, idx) => {
    acc.push(length + (acc.length > 0 ? acc[acc.length - 1] : 0));
    return acc;
  }, []);

  const valueToNeedlePercent = (value) => {
    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      if (value >= range.min && value <= range.max) {
        const prevCumArc = i === 0 ? 0 : cumulativeArcs[i - 1];
        const segmentPercent = (value - range.min) / (range.max - range.min);
        setActiveIndex(i);
        return prevCumArc + segmentPercent * arcsLength[i];
      }
    }
    if (value < ranges[0].min) {
      setActiveIndex(0);
      return 0;
    }
    setActiveIndex(ranges.length - 1);
    return 1;
  };

  return (
    <Box sx={{ width: '80%', p: 2, fontFamily: 'Roboto, sans-serif', color: '#1f2937', mb: 0 }}>
      <ToastContainer />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate('/masters/company')} size="small">
          <ArrowBackIcon sx={{ color: '#635bff' }} />
        </IconButton>

        <Typography
          variant="h5"
          sx={{
            color: '#635bff',
            fontWeight: 600,
            userSelect: 'none',
          }}
        >
          Current Occupancy
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 1.7,
        }}
      >
        {ranges.map((range, index) => (
          <OccupancyLegendItem
            key={index}
            color={range.color}
            borderColor={range.borderColor}
            label={range.label}
            isActive={activeIndex === index}
          />
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <ReactSpeedometer
          key={activeIndex}
          value={rawPercentage.toFixed(2)}
          minValue={0}
          maxValue={100}
          segments={5}
          segmentColors={ranges.map(r => r.color)}
          needleColor={ranges[activeIndex]?.color || "steelblue"}
          needleHeightRatio={0.8}
          needleTransitionDuration={6000}
          needleTransition="easeBounceOut"
          currentValueText={`Occupancy: ${rawPercentage.toFixed(2)}%`}
          width={480}
          height={300}
          ringWidth={40}
          customSegmentLabels={[
            { text: "20%", position: "INSIDE", color: "black", fontSize: "12px" },
            { text: "40%", position: "INSIDE", color: "black", fontSize: "12px" },
            { text: "60%", position: "INSIDE", color: "black", fontSize: "12px" },
            { text: "80%", position: "INSIDE", color: "black", fontSize: "12px" },
            { text: "100%", position: "INSIDE", color: "black", fontSize: "12px" },
          ]}
        />
      </Box>
    </Box>
  );
};

export default TodaysOccupancy;